import type { DespesaExtratoAvaliacaoDetalhe } from "../../../services/FinanceiroService";

export type EnvioCompLegacyPreview = {
  sindicato?: Record<string, unknown>;
  dadosBancarios?: Record<string, unknown>;
  parcelasExtrato?: Record<string, unknown>[];
  comprovantesDespesas?: Record<string, unknown>[];
  tiposDespesas?: { id?: number; descricao?: string; titulo?: string }[];
  possuiSolicitacaoCorrecao?: boolean;
  comprovanteDespesasId?: number | null;
  extratoSolicitacaoId?: number | null;
  statusNome?: string | null;
};

const mapAnaliseToHistorico = (
  analise: unknown,
  despesaEmConformidade: boolean | null = null,
): Record<string, unknown>[] => {
  if (!Array.isArray(analise)) return [];

  const maxId = Math.max(
    0,
    ...analise.map((raw) => Number((raw as Record<string, unknown>)?.id ?? (raw as Record<string, unknown>)?.Id ?? 0)),
  );

  return analise.map((raw) => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const adequadoEm = item.adequadoEm ?? item.AdequadoEm ?? null;
    const obsAnalista =
      item.observacaoAnalista ?? item.ObservacaoAnalista ?? null;
    const emConformidadeItem = item.emConformidade ?? item.EmConformidade;
    const isLatest = Number(item.id ?? item.Id ?? 0) === maxId;

    let conforme: boolean | null = null;
    if (typeof emConformidadeItem === "boolean") conforme = emConformidadeItem;
    else if (isLatest && typeof despesaEmConformidade === "boolean")
      conforme = despesaEmConformidade;
    else if (adequadoEm) conforme = true;
    else if (obsAnalista) conforme = false;

    return {
      id: item.id ?? item.Id,
      obsSindicato: item.observacaoSindicato ?? item.ObservacaoSindicato ?? "",
      obsAnalista: obsAnalista ?? "",
      dataSolicitacao: item.solicitadoEm ?? item.SolicitadoEm ?? null,
      dataCorrecao: adequadoEm,
      conforme,
    };
  });
};

const hasCorrecaoPendente = (despesas: Record<string, unknown>[]): boolean =>
  despesas.some((d) => {
    const hist = d.historicoAnaliseAdequacaoDespesa as Record<string, unknown>[];
    return (hist || []).some(
      (h) =>
        h?.conforme === false &&
        h?.dataSolicitacao != null &&
        String(h.dataSolicitacao).trim() !== "" &&
        (h?.dataCorrecao == null || h?.dataCorrecao === ""),
    );
  });

/** Converte resposta V2 → shape do preview legado consumido por EnvioCompDesp. */
export const mapDespesasExtratoAvaliacaoDetalheToEnvioCompPreview = (
  detalhe: DespesaExtratoAvaliacaoDetalhe,
  solicitacaoLabel = "1ª Solicitação",
): EnvioCompLegacyPreview => {
  const despesas = (detalhe.despesas ?? []).map((raw) => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const tipo = (item.tipoDespesa ?? item.TipoDespesa ?? {}) as Record<
      string,
      unknown
    >;
    const despesaEmConformidade =
      typeof item.emConformidade === "boolean"
        ? item.emConformidade
        : typeof item.EmConformidade === "boolean"
          ? item.EmConformidade
          : null;

    return {
      id: item.id ?? item.Id,
      idTipoDespesa: tipo.id ?? tipo.Id,
      descricao:
        String(item.descricao ?? item.Descricao ?? "").trim() ||
        String(tipo.titulo ?? tipo.Titulo ?? ""),
      valor: item.valor ?? item.Valor ?? 0,
      nomeArquivo: item.nomeArquivo ?? item.NomeArquivo ?? "",
      nomeComprovante: item.nomeArquivo ?? item.NomeArquivo ?? "",
      arquivoId: item.arquivoId ?? item.ArquivoId ?? null,
      historicoAnaliseAdequacaoDespesa: mapAnaliseToHistorico(
        item.analise,
        despesaEmConformidade,
      ),
    };
  });

  return {
    sindicato: {
      cnpj: detalhe.dadosSindicato?.cnpj ?? "",
      nome: detalhe.dadosSindicato?.nome ?? "",
    },
    dadosBancarios: {
      tipo: detalhe.dadosBancarios?.tipo ?? "",
      tipoContaDescricao: String(
        detalhe.dadosBancarios?.tipoContaDescricao ??
          detalhe.dadosBancarios?.tipoConta ??
          detalhe.dadosBancarios?.tipo ??
          "",
      ),
      banco: detalhe.dadosBancarios?.banco ?? "",
      agencia: detalhe.dadosBancarios?.agencia ?? "",
      conta: detalhe.dadosBancarios?.conta ?? "",
    },
    parcelasExtrato: [
      {
        idTipoParcela: 1,
        descricao: solicitacaoLabel,
        valor: detalhe.valorAprovadoSolicitacao ?? 0,
        // Situação/Solicitar não aparecem mais na UI; mantém flags internas
        // para totais e validação de envio (solicitação única do extrato).
        situacao: detalhe.status?.nome ?? "a solicitar",
        solicitar: true,
      },
    ],
    comprovantesDespesas: despesas,
    tiposDespesas: (detalhe.tiposDespesas ?? []).map((t) => ({
      id: t.id,
      descricao: t.titulo ?? "",
      titulo: t.titulo ?? "",
    })),
    possuiSolicitacaoCorrecao: hasCorrecaoPendente(despesas),
    comprovanteDespesasId:
      detalhe.comprovanteDespesasId ??
      detalhe.comprovanteDespesaId ??
      null,
    extratoSolicitacaoId:
      detalhe.extratoSolicitacaoId ??
      detalhe.extratoSolicitacaoAvaliacaoId ??
      null,
    statusNome: detalhe.status?.nome ?? null,
  };
};
